import React, { useCallback, useEffect, useState } from 'react';
import {
  FolderOpen, Upload, FileText, Trash2, Download, Loader2, AlertTriangle,
  ChevronDown, ChevronRight,
} from 'lucide-react';
import toast from 'react-hot-toast';

const CATEGORIES = [
  { value: 'insurance',     label: 'Insurance' },
  { value: 'ast',           label: 'AST / Tenancy' },
  { value: 'mortgage',      label: 'Mortgage' },
  { value: 'loan',          label: 'Loan / Bridging' },
  { value: 'land_registry', label: 'Land Registry' },
  { value: 'epc',           label: 'EPC' },
  { value: 'other',         label: 'Other' },
];

const MAX_BYTES = 25 * 1024 * 1024;

const fmtSize = (n) => {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${Math.round(n / 1024)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
};

const fmtDate = (s) =>
  new Date(s).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });

function authedFetch(url, opts = {}) {
  const token = localStorage.getItem('assetlens_token');
  return fetch(url, {
    ...opts,
    headers: { ...(opts.headers || {}), Authorization: `Bearer ${token}` },
  });
}

/**
 * Per-property documents folder. Investor-only (the property owner).
 * Drop file → choose category → upload. Each file row has a download
 * button that fetches a 15-minute signed URL and opens it.
 *
 * Sharing with consultants is a separate flow (next session).
 */
export default function DocumentsPanel({ userPropertyId }) {
  const [docs, setDocs]         = useState(null);
  const [error, setError]       = useState(null);
  const [open, setOpen]         = useState(false);
  const [uploading, setUploading] = useState(false);
  const [pendingId, setPendingId] = useState(null);
  const [category, setCategory] = useState('other');

  const refresh = useCallback(() => {
    authedFetch(`/api/documents/by-property/${userPropertyId}`)
      .then(r => (r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))))
      .then(d => { setDocs(d); setError(null); })
      .catch(e => setError(e.message));
  }, [userPropertyId]);

  useEffect(() => { if (open) refresh(); }, [open, refresh]);

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (file.size > MAX_BYTES) {
      toast.error(`File too large (${fmtSize(file.size)} > 25 MB)`);
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('user_property_id', String(userPropertyId));
      fd.append('category', category);
      const r = await authedFetch('/api/documents/upload', { method: 'POST', body: fd });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(data.detail || `HTTP ${r.status}`);
      toast.success(`Uploaded ${data.filename}`);
      refresh();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setUploading(false);
    }
  };

  const onDownload = async (doc) => {
    setPendingId(doc.id);
    try {
      const r = await authedFetch(`/api/documents/${doc.id}/url`);
      const data = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(data.detail || `HTTP ${r.status}`);
      window.open(data.url, '_blank', 'noopener');
    } catch (err) {
      toast.error(err.message);
    } finally {
      setPendingId(null);
    }
  };

  const onDelete = async (doc) => {
    if (!window.confirm(`Delete ${doc.filename}? This cannot be undone.`)) return;
    setPendingId(doc.id);
    try {
      const r = await authedFetch(`/api/documents/${doc.id}`, { method: 'DELETE' });
      if (!r.ok && r.status !== 204) {
        const data = await r.json().catch(() => ({}));
        throw new Error(data.detail || `HTTP ${r.status}`);
      }
      toast.success('Deleted');
      refresh();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setPendingId(null);
    }
  };

  return (
    <div className="mt-3 border-t border-slate-800 pt-3">
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="flex items-center gap-2 text-xs text-slate-400 hover:text-slate-200"
      >
        {open ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
        <FolderOpen size={14} />
        <span>Documents{docs ? ` (${docs.length})` : ''}</span>
      </button>

      {open && (
        <div className="mt-3 space-y-2.5">
          {error && (
            <div className="flex items-center gap-2 px-3 py-2 bg-red-500/10 border border-red-500/20 rounded-lg text-red-300 text-xs">
              <AlertTriangle size={12} /> {error}
            </div>
          )}

          {/* Upload row */}
          <div className="flex items-center gap-2 p-2 bg-slate-800/50 rounded-lg">
            <select
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              className="text-xs bg-slate-900 border border-slate-700 rounded px-2 py-1 text-slate-200"
              disabled={uploading}
            >
              {CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
            </select>
            <label className={`flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium cursor-pointer ${
              uploading ? 'bg-slate-800 text-slate-500 cursor-wait' : 'bg-emerald-500/10 text-emerald-300 hover:bg-emerald-500/20'
            }`}>
              {uploading ? <Loader2 size={12} className="animate-spin" /> : <Upload size={12} />}
              {uploading ? 'Uploading…' : 'Upload file'}
              <input
                type="file"
                accept="application/pdf,image/png,image/jpeg,image/webp,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,text/csv"
                onChange={onUpload}
                disabled={uploading}
                className="hidden"
              />
            </label>
          </div>

          {/* Document list */}
          {docs === null ? (
            <p className="text-xs text-slate-500 px-2">Loading…</p>
          ) : docs.length === 0 ? (
            <p className="text-xs text-slate-500 px-2">
              No documents yet. Upload mortgage offers, EPCs, ASTs and other paperwork
              to keep them in one place.
            </p>
          ) : (
            <ul className="space-y-1">
              {docs.map(d => (
                <li key={d.id} className="flex items-center gap-2 px-2 py-1.5 rounded-md hover:bg-slate-800/40 group">
                  <FileText size={14} className="text-slate-500 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="text-xs text-slate-200 truncate" title={d.filename}>
                      {d.filename}
                    </div>
                    <div className="text-[10px] text-slate-500">
                      {CATEGORIES.find(c => c.value === d.category)?.label || d.category} · {fmtSize(d.size_bytes)} · {fmtDate(d.uploaded_at)}
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => onDownload(d)}
                    disabled={pendingId === d.id}
                    className="opacity-0 group-hover:opacity-100 text-slate-400 hover:text-emerald-400 disabled:opacity-50 transition-opacity"
                    title="Download"
                  >
                    {pendingId === d.id ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />}
                  </button>
                  <button
                    type="button"
                    onClick={() => onDelete(d)}
                    disabled={pendingId === d.id}
                    className="opacity-0 group-hover:opacity-100 text-slate-400 hover:text-rose-400 disabled:opacity-50 transition-opacity"
                    title="Delete"
                  >
                    <Trash2 size={14} />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
